/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * AvmReadiness.js  —  TEMPORARY internal tool (admin dash).
 *
 * Two modes:
 *  - Readiness: funnel (sold -> +condition -> +lauftq) + trainable-row table +
 *    CSV export. Tells you whether it's a real train or a thesis-check.
 *  - Fill by Zip: pull off-market/sold properties in a zip, see what each is
 *    missing (sold price / condition / lauftq), and fill any gap on demand.
 *
 * All model output, no human labels. Display + on-demand fill. Delete when done.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  RefreshCw, AlertCircle, Loader, ChevronLeft, ChevronRight, Download,
  Search, Check, DollarSign, Eye, Home,
} from 'react-feather';
import AuthService from '../../services/authService';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

const C = {
  bg: '#fcfcfc', panel: '#eeebe3', accent: '#fdd700', ink: '#1a1a1a',
  sage: '#7a9471', border: '#e2ddd2', muted: '#8a8578',
};

const COND_ANCHORS = ['Dated', 'Builder-grade', 'Updated', 'Renovated', 'Luxury'];
const LAUFTQ_AXES = [
  { key: 'dynamicVsCalm', low: 'Dyn', high: 'Calm' },
  { key: 'modernVsTraditional', low: 'Mod', high: 'Trad' },
  { key: 'socialVsPrivate', low: 'Soc', high: 'Priv' },
  { key: 'naturalVsEngineered', low: 'Nat', high: 'Eng' },
];

const fmtPrice = (n) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-US');
};
const authHeaders = () => ({ Authorization: `Bearer ${AuthService.getToken()}` });

// Transient toast for prominent success/error feedback. Auto-dismisses.
const useToast = () => {
  const [toast, setToast] = useState(null); // { type:'success'|'error', msg }
  const timerRef = useRef(null);
  const notify = useCallback((type, msg) => {
    setToast({ type, msg, key: Date.now() });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setToast(null), 4500);
  }, []);
  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);
  return [toast, notify];
};

const Toast = ({ toast }) => {
  if (!toast) return null;
  const ok = toast.type === 'success';
  return (
    <div style={{ ...S.toast, ...(ok ? S.toastOk : S.toastErr) }}>
      {ok ? <Check size={16} /> : <AlertCircle size={16} />}
      <span>{toast.msg}</span>
    </div>
  );
};

const AvmReadiness = () => {
  const [mode, setMode] = useState('readiness'); // 'readiness' | 'fill'

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <h2 style={{ margin: 0, color: C.ink }}>AVM Readiness</h2>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
            {mode === 'readiness'
              ? 'Properties trainable right now: sold + condition + LauftQ. All model output.'
              : 'Fill missing data zip by zip: sold price, condition, and LauftQ.'}
          </div>
        </div>
        <div style={S.modeToggle}>
          <button
            style={mode === 'readiness' ? S.modeOn : S.modeOff}
            onClick={() => setMode('readiness')}
          >
            Readiness
          </button>
          <button
            style={mode === 'fill' ? S.modeOn : S.modeOff}
            onClick={() => setMode('fill')}
          >
            Fill by Zip
          </button>
        </div>
      </div>

      {mode === 'readiness' ? <ReadinessView /> : <FillByZipView />}

      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

// ============================================================
// READINESS VIEW (funnel + trainable table + CSV export)
// ============================================================
const ReadinessView = () => {
  const [funnel, setFunnel] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [toast, notify] = useToast();
  const [zip, setZip] = useState('');

  const zipParam = /^\d{5}$/.test(zip.trim()) ? `&zip=${zip.trim()}` : '';

  const load = useCallback(async (p) => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/admin/labeling/avm-readiness?page=${p}&limit=25${zipParam}`, { headers: authHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to load');
      setFunnel(json.data.funnel); setRows(json.data.rows);
      setTotalPages(json.data.totalPages); setTotal(json.data.total); setPage(json.data.page);
    } catch (e) { setError(e.message); notify('error', `Couldn't load readiness: ${e.message}`); } finally { setLoading(false); }
  }, [notify, zipParam]);

  useEffect(() => { load(1); }, [load]);

  const downloadCsv = useCallback(async () => {
    setExporting(true); setError('');
    try {
      const res = await fetch(`${API_URL}/admin/labeling/avm-readiness/export.csv${zipParam ? `?${zipParam.slice(1)}` : ''}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `avm_training${zipParam ? `_${zip.trim()}` : ''}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      notify('success', `Training CSV downloaded${zipParam ? ` (zip ${zip.trim()})` : ''}`);
    } catch (e) { setError(e.message); notify('error', `Export failed: ${e.message}`); } finally { setExporting(false); }
  }, [notify, zipParam, zip]);

  const dropToCond = funnel && funnel.sold > 0
    ? Math.round(((funnel.sold - funnel.soldAndCondition) / funnel.sold) * 100) : 0;
  const dropToLq = funnel && funnel.soldAndCondition > 0
    ? Math.round(((funnel.soldAndCondition - funnel.trainable) / funnel.soldAndCondition) * 100) : 0;

  return (
    <>
      <Toast toast={toast} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={S.zipFilter}>
          <Search size={15} color={C.muted} />
          <input
            style={S.zipInput}
            placeholder="Filter by zip (optional)…"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
            onKeyDown={(e) => e.key === 'Enter' && load(1)}
          />
          {zip && (
            <button style={S.zipClear} onClick={() => { setZip(''); setTimeout(() => load(1), 0); }} title="Clear filter">×</button>
          )}
          <button style={S.zipGo} onClick={() => load(1)} disabled={loading}>Apply</button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.refreshBtn} onClick={downloadCsv} disabled={exporting || loading}>
            {exporting ? <Loader size={15} className="spin" /> : <Download size={15} />} Export CSV
          </button>
          <button style={S.refreshBtn} onClick={() => load(page)} disabled={loading}>
            {loading ? <Loader size={15} className="spin" /> : <RefreshCw size={15} />} Refresh
          </button>
        </div>
      </div>

      {error && <div style={S.errorBar}><AlertCircle size={16} /> {error}</div>}

      {funnel && zipParam && (
        <div style={{ fontSize: 12, color: C.sage, fontWeight: 600, marginBottom: 8 }}>
          Scoped to zip {zip.trim()} — funnel, table, and CSV reflect this zip only.
        </div>
      )}

      {funnel && (
        <div style={S.funnel}>
          <div style={S.stage}><div style={S.stageNum}>{funnel.sold.toLocaleString()}</div><div style={S.stageLabel}>Sold w/ price</div></div>
          <div style={S.arrow}>→<span style={S.dropTag}>−{dropToCond}%</span></div>
          <div style={S.stage}><div style={S.stageNum}>{funnel.soldAndCondition.toLocaleString()}</div><div style={S.stageLabel}>+ Condition</div></div>
          <div style={S.arrow}>→<span style={S.dropTag}>−{dropToLq}%</span></div>
          <div style={{ ...S.stage, ...S.stageFinal }}><div style={{ ...S.stageNum, color: C.sage }}>{funnel.trainable.toLocaleString()}</div><div style={S.stageLabel}>Trainable</div></div>
        </div>
      )}

      {funnel && (
        <div style={S.hint}>
          {funnel.trainable < 1000
            ? `Below ~1,000 — a train here is a thesis-check, not production. Biggest drop is ${dropToCond >= dropToLq ? 'sold → condition' : 'condition → LauftQ'}; close it via Fill by Zip or more ${dropToCond >= dropToLq ? 'condition auto-assign' : 'vision batch-score'}.`
            : `${funnel.trainable.toLocaleString()} trainable rows — enough for a real first train.`}
        </div>
      )}

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Address</th><th style={S.thR}>Sold</th>
            <th style={S.thC}>Bd</th><th style={S.thC}>Ba</th><th style={S.thR}>Sqft</th>
            <th style={S.thC}>Kit</th><th style={S.thC}>Bath</th><th style={S.thC}>Ext</th><th style={S.thC}>Liv</th>
            {LAUFTQ_AXES.map((a) => <th key={a.key} style={S.thC} title={`${a.low} ↔ ${a.high}`}>{a.low}/{a.high}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={S.tr}>
                <td style={S.td}><div style={S.addr}>{r.address}</div><div style={S.sub}>{r.csz} · sold {fmtDate(r.soldDate)}</div></td>
                <td style={S.tdR}>{fmtPrice(r.soldPrice)}</td>
                <td style={S.tdC}>{r.beds ?? '—'}</td><td style={S.tdC}>{r.baths ?? '—'}</td>
                <td style={S.tdR}>{r.sqft ? r.sqft.toLocaleString() : '—'}</td>
                <td style={S.tdC}>{condCell(r.condition.kitchen)}</td>
                <td style={S.tdC}>{condCell(r.condition.bath)}</td>
                <td style={S.tdC}>{condCell(r.condition.exterior)}</td>
                <td style={S.tdC}>{condCell(r.condition.living)}</td>
                {LAUFTQ_AXES.map((a) => <td key={a.key} style={S.tdC}>{r.lauftq[a.key] == null ? '—' : r.lauftq[a.key]}</td>)}
              </tr>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan={13} style={S.empty}>No trainable rows yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div style={S.pager}>
          <button style={S.pageBtn} onClick={() => load(page - 1)} disabled={page <= 1 || loading}><ChevronLeft size={16} /></button>
          <span style={{ color: C.muted, fontSize: 13 }}>Page {page} / {totalPages} · {total.toLocaleString()} rows</span>
          <button style={S.pageBtn} onClick={() => load(page + 1)} disabled={page >= totalPages || loading}><ChevronRight size={16} /></button>
        </div>
      )}
    </>
  );
};

// ============================================================
// FILL BY ZIP VIEW
// ============================================================
const FillByZipView = () => {
  const [zip, setZip] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // per-action state, keyed `${id}:${action}` -> 'loading' | { ok, msg }
  const [act, setAct] = useState({});
  const [toast, notify] = useToast();
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const [batchSold, setBatchSold] = useState(false);

  const loadZip = useCallback(async () => {
    if (!/^\d{5}$/.test(zip.trim())) { setError('Enter a 5-digit zip'); return; }
    setLoading(true); setError(''); setRows([]); setSummary(null); setAct({});
    try {
      const res = await fetch(`${API_URL}/admin/labeling/avm-fill/by-zip?zip=${zip.trim()}`, { headers: authHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to load');
      setRows(json.data.rows); setSummary(json.data.summary);
      const n = json.data.summary.total;
      notify(n > 0 ? 'success' : 'error',
        n > 0 ? `Loaded ${n} off-market/sold in ${zip.trim()}` : `No off-market/sold properties in ${zip.trim()}`);
    } catch (e) { setError(e.message); notify('error', `Couldn't load zip: ${e.message}`); } finally { setLoading(false); }
  }, [zip, notify]);

  const runBatchSold = useCallback(async () => {
    if (!/^\d{5}$/.test(zip.trim())) { notify('error', 'Load a zip first'); return; }
    setBatchSold(true);
    try {
      const res = await fetch(`${API_URL}/admin/labeling/avm-fill/sold-price-zip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ zip: zip.trim() }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Batch failed');
      const d = json.data;
      notify(d.filled > 0 ? 'success' : 'error',
        `Sold $: ${d.filled} filled · ${d.noPrice} no price · ${d.review} review · ${d.unmatched} unmatched (listing-id) of ${d.checked}`);
      await loadZip(); // refresh rows + summary to reflect writes
    } catch (e) { notify('error', `Batch sold failed: ${e.message}`); } finally { setBatchSold(false); }
  }, [zip, notify, loadZip]);

  const runProbe = useCallback(async () => {
    if (!/^\d{5}$/.test(zip.trim())) { notify('error', 'Enter a 5-digit zip first'); return; }
    setProbing(true); setProbe(null);
    try {
      const res = await fetch(`${API_URL}/admin/labeling/avm-fill/probe?zip=${zip.trim()}`, { headers: authHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Probe failed');
      setProbe(json.data);
      notify('success', 'Probe done — see raw output below');
    } catch (e) { notify('error', `Probe failed: ${e.message}`); } finally { setProbing(false); }
  }, [zip, notify]);

  const setActState = (id, action, val) =>
    setAct((prev) => ({ ...prev, [`${id}:${action}`]: val }));

  // apply the fill result to the in-memory row so the UI updates without reload
  const applyResult = (id, action, data) => {
    setRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      const g = { ...r.gaps };
      if (data.filled && action === 'sold-price') g.soldPrice = { done: true, value: data.soldPrice };
      if (data.filled && action === 'lauftq') g.lauftq = { done: true };
      if (data.filled && action === 'condition') g.condition = { done: true, rooms: data.rooms };
      return { ...r, gaps: g };
    }));
  };

  const ACTION_LABEL = { 'sold-price': 'Sold price', lauftq: 'LauftQ', condition: 'Condition' };

  const runFill = async (row, action) => {
    const id = row.id;
    const short = (row.address || '').split(',')[0];
    setActState(id, action, 'loading');
    try {
      const res = await fetch(`${API_URL}/admin/labeling/avm-fill/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ propertyId: id }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed');
      const data = json.data;
      applyResult(id, action, data);

      let inline, toastMsg;
      if (data.filled) {
        if (action === 'sold-price') { inline = fmtPrice(data.soldPrice); toastMsg = `${short}: sold ${fmtPrice(data.soldPrice)}`; }
        else if (action === 'lauftq') { inline = 'done'; toastMsg = `${short}: LauftQ scored (${data.roomsAnalyzed} rooms)`; }
        else { inline = 'done'; toastMsg = `${short}: condition scored (${data.rooms} rooms)`; }
      } else {
        inline = data.reason || 'no result';
        toastMsg = `${short}: ${ACTION_LABEL[action]} — ${data.reason || 'no result'}`;
      }
      setActState(id, action, { ok: !!data.filled, msg: inline });
      notify(data.filled ? 'success' : 'error', toastMsg);
    } catch (e) {
      setActState(id, action, { ok: false, msg: e.message });
      notify('error', `${short}: ${ACTION_LABEL[action]} failed — ${e.message}`);
    }
  };

  const cell = (r, action, done, label, Icon, doneNode, disabled, disabledHint) => {
    const st = act[`${r.id}:${action}`];
    if (done) return doneNode;
    if (st === 'loading') return <span style={S.cellBusy}><Loader size={13} className="spin" /></span>;
    return (
      <div>
        <button
          style={disabled ? S.fillBtnDisabled : S.fillBtn}
          onClick={() => runFill(r, action)}
          disabled={disabled}
          title={disabled ? disabledHint : ''}
        >
          <Icon size={12} /> {label}
        </button>
        {st && st !== 'loading' && (
          <div style={{ ...S.actMsg, color: st.ok ? C.sage : '#b07a3c' }}>{st.msg}</div>
        )}
      </div>
    );
  };

  return (
    <>
      <Toast toast={toast} />
      <div style={S.searchBar}>
        <Search size={18} color={C.muted} />
        <input
          style={S.searchInput}
          placeholder="Enter a 5-digit zip (e.g. 80004)…"
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
          onKeyDown={(e) => e.key === 'Enter' && loadZip()}
        />
        <button style={S.loadBtn} onClick={loadZip} disabled={loading}>
          {loading ? <Loader size={15} className="spin" /> : 'Load zip'}
        </button>
        <button style={S.probeBtn} onClick={runProbe} disabled={probing} title="Diagnostic: see which API call returns sold price">
          {probing ? <Loader size={15} className="spin" /> : 'Probe API'}
        </button>
        <button style={S.batchBtn} onClick={runBatchSold} disabled={batchSold || !summary} title="One PropertySearch call fills every missing sold price in this zip">
          {batchSold ? <Loader size={15} className="spin" /> : <DollarSign size={15} />} Fill all sold $
        </button>
      </div>

      {probe && (
        <div style={S.probeWrap}>
          <div style={S.probeHead}>
            Sold-API probe · zip {probe.zip} · {(probe.ourReApiIds || []).length} reApiIds
            <button style={S.probeClose} onClick={() => setProbe(null)}>close</button>
          </div>
          <pre style={S.probePre}>{JSON.stringify(probe, null, 2)}</pre>
        </div>
      )}

      {error && <div style={S.errorBar}><AlertCircle size={16} /> {error}</div>}

      {summary && (
        <div style={S.summaryRow}>
          <SummaryTile n={summary.total} label="Off-market / sold" />
          <SummaryTile n={summary.missingSold} label="Missing sold $" warn />
          <SummaryTile n={summary.missingLauftq} label="Missing LauftQ" warn />
          <SummaryTile n={summary.missingCondition} label="Missing condition" warn />
          <SummaryTile n={summary.trainable} label="Trainable now" good />
        </div>
      )}

      {rows.length > 0 && (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Address</th>
              <th style={S.thC}>Status</th>
              <th style={S.thC}>Sold price</th>
              <th style={S.thC}>LauftQ</th>
              <th style={S.thC}>Condition</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={S.tr}>
                  <td style={S.td}>
                    <div style={S.addr}>{r.address}</div>
                    <div style={S.sub}>{r.csz}{r.sqft ? ` · ${r.sqft.toLocaleString()} sqft` : ''}</div>
                  </td>
                  <td style={S.tdC}><span style={S.statusBadge}>{r.status || '—'}</span></td>

                  {/* Sold price */}
                  <td style={S.tdC}>
                    {cell(
                      r, 'sold-price', r.gaps.soldPrice.done, 'Get sold $', DollarSign,
                      <span style={S.doneVal}><Check size={12} /> {fmtPrice(r.gaps.soldPrice.value)}</span>,
                      !r.hasReApiId, 'No reApiId — cannot look up'
                    )}
                  </td>

                  {/* LauftQ */}
                  <td style={S.tdC}>
                    {cell(
                      r, 'lauftq', r.gaps.lauftq.done, 'Get LauftQ', Eye,
                      <span style={S.doneVal}><Check size={12} /> scored</span>,
                      false, ''
                    )}
                  </td>

                  {/* Condition (needs LauftQ first) */}
                  <td style={S.tdC}>
                    {cell(
                      r, 'condition', r.gaps.condition.done, 'Get condition', Home,
                      <span style={S.doneVal}><Check size={12} /> {r.gaps.condition.rooms} rms</span>,
                      !r.gaps.lauftq.done, 'Run LauftQ first'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {summary && rows.length === 0 && !loading && (
        <div style={S.empty}>No off-market / sold properties found in {zip}.</div>
      )}
    </>
  );
};

const SummaryTile = ({ n, label, warn, good }) => (
  <div style={S.sTile}>
    <div style={{ ...S.sNum, color: good ? C.sage : warn && n > 0 ? '#b07a3c' : C.ink }}>{n.toLocaleString()}</div>
    <div style={S.sLabel}>{label}</div>
  </div>
);

const condCell = (v) => {
  if (v == null) return <span style={{ color: C.muted }}>—</span>;
  return <span style={S.condBadge} title={COND_ANCHORS[v - 1]}>{v}</span>;
};

const S = {
  wrap: { padding: 24, background: C.bg, minHeight: '100%', fontFamily: 'system-ui, sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 },
  modeToggle: { display: 'flex', background: C.panel, borderRadius: 9, padding: 3, flexShrink: 0 },
  modeOn: { background: '#fff', color: C.ink, border: 'none', borderRadius: 7, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' },
  modeOff: { background: 'transparent', color: C.muted, border: 'none', borderRadius: 7, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  refreshBtn: { display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: 13, color: C.ink, cursor: 'pointer', flexShrink: 0 },
  zipFilter: { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 9, padding: '6px 10px' },
  zipInput: { border: 'none', outline: 'none', fontSize: 14, background: 'transparent', color: C.ink, width: 150 },
  zipClear: { background: 'transparent', border: 'none', color: C.muted, fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: '0 2px' },
  zipGo: { background: C.ink, color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontWeight: 600, fontSize: 12, cursor: 'pointer' },
  errorBar: { display: 'flex', alignItems: 'center', gap: 8, background: '#fdecea', color: '#b71c1c', border: '1px solid #f5c6cb', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 14 },
  funnel: { display: 'flex', alignItems: 'stretch', gap: 4, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 10 },
  stage: { flex: 1, textAlign: 'center', padding: '8px 4px', borderRadius: 8, background: C.bg },
  stageFinal: { background: '#f1f5ef', border: `1px solid ${C.sage}33` },
  stageNum: { fontSize: 26, fontWeight: 700, color: C.ink, lineHeight: 1.1 },
  stageLabel: { fontSize: 12, color: C.muted, marginTop: 4 },
  arrow: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: 18, padding: '0 4px', gap: 2 },
  dropTag: { fontSize: 10, color: '#b07a3c', fontWeight: 600 },
  hint: { fontSize: 13, color: C.muted, marginBottom: 18, lineHeight: 1.4 },
  searchBar: { display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', marginBottom: 14 },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: 15, background: 'transparent', color: C.ink },
  loadBtn: { background: C.accent, color: C.ink, border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, cursor: 'pointer', minWidth: 86 },
  probeBtn: { background: '#fff', color: C.ink, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer', minWidth: 86 },
  batchBtn: { display: 'inline-flex', alignItems: 'center', gap: 5, background: C.sage, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  probeWrap: { background: '#1a1a1a', borderRadius: 10, marginBottom: 16, overflow: 'hidden' },
  probeHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fdd700', fontSize: 12, fontWeight: 600, padding: '8px 12px', borderBottom: '1px solid #333' },
  probeClose: { background: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: 6, padding: '2px 8px', fontSize: 11, cursor: 'pointer' },
  probePre: { margin: 0, padding: 12, color: '#d6e9cf', fontSize: 11, lineHeight: 1.4, maxHeight: 360, overflow: 'auto', fontFamily: 'ui-monospace, Menlo, monospace' },
  summaryRow: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  sTile: { flex: 1, minWidth: 120, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px' },
  sNum: { fontSize: 24, fontWeight: 700, lineHeight: 1.1 },
  sLabel: { fontSize: 12, color: C.muted, marginTop: 4 },
  tableWrap: { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
  thR: { textAlign: 'right', padding: '10px 12px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
  thC: { textAlign: 'center', padding: '10px 8px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
  tr: { borderBottom: `1px solid ${C.border}` },
  td: { padding: '10px 12px', color: C.ink, verticalAlign: 'top' },
  tdR: { padding: '10px 12px', color: C.ink, textAlign: 'right', whiteSpace: 'nowrap' },
  tdC: { padding: '10px 8px', color: C.ink, textAlign: 'center', verticalAlign: 'top' },
  addr: { fontWeight: 600 },
  sub: { fontSize: 11, color: C.muted, marginTop: 2 },
  condBadge: { display: 'inline-block', minWidth: 20, background: C.sage, color: '#fff', borderRadius: 5, padding: '2px 6px', fontWeight: 600, fontSize: 12 },
  statusBadge: { display: 'inline-block', background: C.panel, color: C.ink, borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 },
  fillBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, background: C.accent, color: C.ink, border: 'none', borderRadius: 7, padding: '6px 10px', fontWeight: 600, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' },
  fillBtnDisabled: { display: 'inline-flex', alignItems: 'center', gap: 4, background: C.panel, color: C.muted, border: 'none', borderRadius: 7, padding: '6px 10px', fontWeight: 600, fontSize: 12, cursor: 'not-allowed', whiteSpace: 'nowrap' },
  cellBusy: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: C.sage },
  doneVal: { display: 'inline-flex', alignItems: 'center', gap: 4, color: C.sage, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' },
  actMsg: { fontSize: 10, marginTop: 4, maxWidth: 130, lineHeight: 1.3 },
  empty: { padding: 32, textAlign: 'center', color: C.muted },
  toast: { display: 'flex', alignItems: 'center', gap: 8, borderRadius: 9, padding: '11px 14px', marginBottom: 12, fontSize: 14, fontWeight: 600, animation: 'none' },
  toastOk: { background: '#f1f5ef', color: '#3f5c37', border: `1px solid ${C.sage}66` },
  toastErr: { background: '#fdecea', color: '#b71c1c', border: '1px solid #f5c6cb' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginTop: 16 },
  pageBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', color: C.ink },
};

export default AvmReadiness;